import { createFileRoute, redirect, useParams } from "@tanstack/react-router";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { LangProvider } from "../contexts/LangContext";
import { faqJsonLdScript } from "../data/faq";
import { FaqPage } from "../pages/FaqPage";
import { buildCompleteHead } from "../utils/seo";

const allowedLanguages = ["oz", "uz", "ru", "en"];

const langMapForContext: Record<string, string> = {
  oz: "uz-Latn",
  uz: "uz-Cyrl",
  ru: "ru",
  en: "en-US",
};

export const Route = createFileRoute("/$lang/faq")({
  head: ({ params }) => {
    const currentPath = `/${params.lang}/faq`;
    const canonicalUrl = `https://lotin.uz${currentPath}`;
    const seoData = buildCompleteHead(
      params.lang as "oz" | "uz" | "ru" | "en",
      "faq",
      canonicalUrl,
      currentPath,
      currentPath,
    );

    return {
      meta: [{ title: seoData.title }, ...seoData.meta],
      links: seoData.links,
      scripts: [faqJsonLdScript(langMapForContext[params.lang] || "uz-Latn")],
    };
  },
  // Cast as any to satisfy older `beforeLoad` typing that expects `never`
  beforeLoad: ((ctx: { params: { lang: string } }) => {
    const { params } = ctx;
    if (!allowedLanguages.includes(params.lang)) {
      throw redirect({ to: "/faq" });
    }
    if (params.lang === "oz") {
      throw redirect({ to: "/faq" });
    }
  }) as any,
  component: Faq,
});

function Faq() {
  const { lang } = useParams({ from: "/$lang/faq" });
  const { i18n } = useTranslation();

  useEffect(() => {
    const langMap: Record<string, string> = {
      oz: "uz-Latn", // Uzbek Latin (default)
      uz: "uz-Cyrl", // Uzbek Cyrillic
      ru: "ru", // Russian
      en: "en", // English (matches the hreflang code)
    };
    document.documentElement.lang = langMap[lang] || "uz-Latn";
    // Guard for SSR: only call changeLanguage if it exists
    if (i18n?.changeLanguage) {
      i18n.changeLanguage(lang);
    }
  }, [lang, i18n]);

  return (
    <LangProvider lang={langMapForContext[lang] || "uz-Latn"}>
      <FaqPage lang={langMapForContext[lang]} />
    </LangProvider>
  );
}
